'use client';

import { setRuntimeLogMaskingEnabled } from '@/shared/logging/json-logger';

export interface FrontendRuntimeConfig {
  runtime: {
    logMaskingEnabled: boolean;
  };
  vworld: {
    apiKey: string;
    domain: string;
  };
}

let runtimeConfigPromise: Promise<FrontendRuntimeConfig> | null = null;

function isRuntimeConfig(value: unknown): value is FrontendRuntimeConfig {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Partial<FrontendRuntimeConfig>;
  return typeof record.vworld?.apiKey === 'string' && typeof record.vworld?.domain === 'string' && typeof record.runtime?.logMaskingEnabled === 'boolean';
}

/**
 * 프론트엔드 런타임 설정을 조회하고 브라우저 전역 설정에 반영합니다.
 */
export async function fetchFrontendRuntimeConfig(): Promise<FrontendRuntimeConfig> {
  const response = await fetch('/api/runtime-config', {
    cache: 'no-store',
    credentials: 'include',
  });

  const payload = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'error' in payload ? String((payload as { error?: unknown }).error ?? '') : '';
    throw new Error(message || '프론트엔드 런타임 설정을 조회하지 못했습니다.');
  }

  if (!isRuntimeConfig(payload)) {
    throw new Error('프론트엔드 런타임 설정 응답 형식이 올바르지 않습니다.');
  }

  setRuntimeLogMaskingEnabled(payload.runtime.logMaskingEnabled);
  return payload;
}

/**
 * 한 화면 수명 안에서 중복 호출을 줄이기 위해 런타임 설정 조회 Promise를 재사용합니다.
 */
export function getFrontendRuntimeConfig(): Promise<FrontendRuntimeConfig> {
  runtimeConfigPromise ??= fetchFrontendRuntimeConfig().catch(error => {
    runtimeConfigPromise = null;
    throw error;
  });

  return runtimeConfigPromise;
}

/**
 * 관리자 저장 직후 새 설정을 다시 읽기 위해 캐시를 비웁니다.
 */
export function resetFrontendRuntimeConfigCache(): void {
  runtimeConfigPromise = null;
}
